import {
  Button,
  Checkbox,
  Flex,
  IconCheckLine,
  Metric,
  MetricGroup,
  Pill,
  ScreenReaderContent,
  SimpleSelect,
  Spinner,
  Table,
  Text,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GoalObjectiveTreeSelect from "../../../components/Selectors/GoalObjectiveTreeSelect";
import { ExternalService } from "../../../services/ExternalService";
import { TermsContext } from "../../../utils/Context";
import {
  buildGoalTreeFromMeasures,
  NO_OBJECTIVE_ID,
} from "../../../utils/buildGoalTreeFromMeasures";
import { sort } from "../../../utils/utils";
import GroupByToggle from "../GroupByToggle";
import {
  groupItemsByGoalObjective,
  groupItemsBySection,
} from "../utils/groupingUtils";

const DataCollectionStatus = ({
  plan,
  onAddToAgenda,
  agenda,
  groupBy,
  setGroupBy,
}) => {
  const { t } = useTranslation();
  const { insightsTerms: contextInsightsTerms, canvasTerms } =
    useContext(TermsContext);

  // For external access, fetch insights terms if context is empty
  const [externalInsightsTerms, setExternalInsightsTerms] = useState([]);
  const [termsLoading, setTermsLoading] = useState(
    !contextInsightsTerms || contextInsightsTerms.length === 0
  );
  useEffect(() => {
    if ((!contextInsightsTerms || contextInsightsTerms.length === 0) && plan) {
      setTermsLoading(true);
      ExternalService.getInsightsTerms()
        .then((terms) => setExternalInsightsTerms(terms || []))
        .catch(() => setExternalInsightsTerms([]))
        .finally(() => setTermsLoading(false));
    } else {
      setTermsLoading(false);
    }
  }, [contextInsightsTerms, plan]);

  // Use whichever source has data
  const insightsTerms =
    contextInsightsTerms && contextInsightsTerms.length > 0
      ? contextInsightsTerms
      : externalInsightsTerms;

  const [selectedSection, setSelectedSection] = useState("");
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);
  const [selectedObjectiveIds, setSelectedObjectiveIds] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Set of assessment IDs already in the agenda — pre-select and disable their checkboxes
  const agendaSourceIds = useMemo(() => {
    return new Set(
      agenda.items
        .filter((i) => i.source_type === "assessment")
        .map((i) => i.source_id)
    );
  }, [agenda]);
  // Aggregate assessments into a flat array for goal tree building
  const sections = plan?.sections;
  const assessments = useMemo(() => {
    if (!sections) return [];
    return sections.flatMap((section) => section.assessments || []);
  }, [sections]);

  // Build goals tree from assessments (same shape as measures: { goal, objective })
  const goals = useMemo(
    () => buildGoalTreeFromMeasures(assessments),
    [assessments]
  );

  // Handle goal/objective tree selection changes
  const handleGoalObjectiveChange = ({
    goalIds = [],
    objectiveIds = [],
  } = {}) => {
    setSelectedGoalIds(goalIds);
    setSelectedObjectiveIds(objectiveIds);
  };

  // Compute effective grouping mode - force "section" if plan doesn't support goals/objectives
  const effectiveGroupBy = plan.include_goals_objs ? groupBy : "section";

  // Handle group by toggle change with clearing of goal/objective selections
  const handleGroupByChange = (newValue) => {
    if (newValue === "section" && groupBy === "goalObjective") {
      setSelectedGoalIds([]);
      setSelectedObjectiveIds([]);
    }
    setGroupBy(newValue);
  };

  // Build collection items from assessments
  const collectionItems = useMemo(() => {
    if (!plan?.sections) return [];

    // Build a local insights terms lookup from the plan's own term data
    // This allows resolving assessing_terms IDs even without TermsContext
    const localInsightsTermMap = {};
    plan.sections.forEach((section) => {
      (section.assessments || []).forEach((assessment) => {
        (assessment.terms || []).forEach((term) => {
          if (term.id || term.ddl_id) {
            const id = term.id || term.ddl_id;
            localInsightsTermMap[id] = term;
          }
        });
      });
    });

    const items = [];

    plan.sections.forEach((section) => {
      const sectionName = section.name || "";

      if (section.assessments) {
        let sectionSeq = 0;
        section.assessments.forEach((assessment) => {
          sectionSeq++;
          const isExternal = assessment.assessment_type === "external";
          const terms = assessment.terms || [];

          // Build term status list from canvas terms (the actual collection terms)
          // Each insights term may contain canvas_terms[] — those are the real collection terms
          // Fall back to insights term end_at for completion if canvas term has no end_at
          const termStatuses = [];
          terms.forEach((term) => {
            const insightsEndAt = term.end_at || null;
            // Also resolve via context if needed
            let resolvedInsightsEndAt = insightsEndAt;
            if (
              !resolvedInsightsEndAt &&
              term.insights_term_id &&
              insightsTerms
            ) {
              const it = insightsTerms.find(
                (i) => i.id === term.insights_term_id
              );
              if (it) resolvedInsightsEndAt = it.end_at;
            }

            const termCanvasTerms = term.canvas_terms || [];

            if (termCanvasTerms.length > 0) {
              termCanvasTerms.forEach((ct) => {
                const ctEndAt = ct.end_at || resolvedInsightsEndAt;
                const ended = ctEndAt ? new Date(ctEndAt) < new Date() : false;
                termStatuses.push({ name: ct.name || term.name || "", ended });
              });
            } else {
              // No canvas terms — show the insights term itself as fallback
              const ended = resolvedInsightsEndAt
                ? new Date(resolvedInsightsEndAt) < new Date()
                : false;
              termStatuses.push({ name: term.name || "", ended });
            }
          });

          // For external assessments, resolve assessing_terms
          if (
            isExternal &&
            assessment.assessing_terms &&
            assessment.assessing_terms.length > 0 &&
            termStatuses.length === 0
          ) {
            assessment.assessing_terms.forEach((termId) => {
              // First try canvasTerms context (internal access)
              if (canvasTerms && canvasTerms.length > 0) {
                const ct = canvasTerms.find((c) => c.id === termId);
                if (ct) {
                  const insightsTerm = insightsTerms.find(
                    (it) => it.id === ct.insights_term_id
                  );
                  const name = insightsTerm
                    ? insightsTerm.name
                    : ct.name || termId;
                  const endAt = insightsTerm ? insightsTerm.end_at : ct.end_at;
                  const ended = endAt ? new Date(endAt) < new Date() : false;
                  termStatuses.push({ name, ended });
                  return;
                }
              }
              // Fallback: search insights terms' canvas_terms for this ID
              if (insightsTerms && insightsTerms.length > 0) {
                for (const it of insightsTerms) {
                  const found = (it.canvas_terms || []).find(
                    (ct) => ct.id === termId || ct.ddl_id === termId
                  );
                  if (found) {
                    const ended = it.end_at
                      ? new Date(it.end_at) < new Date()
                      : false;
                    termStatuses.push({ name: it.name || termId, ended });
                    return;
                  }
                }
              }
            });
          }

          const totalTerms = termStatuses.length;
          const completedTerms = termStatuses.filter((ts) => ts.ended).length;
          const percentage =
            totalTerms > 0
              ? Math.round((completedTerms / totalTerms) * 100)
              : 0;

          // Determine assessment type label for generated name
          let typeLabel;
          if (isExternal) {
            typeLabel =
              assessment.purl_type === "juried"
                ? t(
                    "assessmentPlanningDashboard.dataCollection.externalJuriedAssessment"
                  )
                : t(
                    "assessmentPlanningDashboard.dataCollection.externalAssessment"
                  );
          } else if (assessment.collecting_evidence) {
            typeLabel = t(
              "assessmentPlanningDashboard.dataCollection.canvasJuriedAssessment"
            );
          } else if (assessment.assessment_type === "flexible") {
            typeLabel = t(
              "assessmentPlanningDashboard.dataCollection.flexibleCanvasAssessment"
            );
          } else {
            typeLabel = t(
              "assessmentPlanningDashboard.dataCollection.targetedCanvasAssessment"
            );
          }

          // Generate measure name: #seq - TypeLabel
          const measureName = `#${sectionSeq} - ${typeLabel}`;

          // Courses / offerings info — use course_code like the plan toggles grid
          const courses = assessment.offerings
            ? assessment.offerings
                .map((o) => o.code || o.name || "")
                .filter(Boolean)
            : [];

          // Rubric name if available
          const rubricName = assessment.assignment_rubric?.name || "";

          items.push({
            id: assessment.id,
            sectionName,
            measureName,
            isExternal,
            courses,
            rubricName,
            termStatuses,
            totalTerms,
            completedTerms,
            percentage,
            sectionDescription: section.description || "",
            goalId: assessment.goal?.id || null,
            goalDescription: assessment.goal?.description || "",
            objectiveId: assessment.objective?.id || null,
            objectiveDescription: assessment.objective?.description || "",
          });
        });
      }
    });

    return items;
  }, [plan, insightsTerms, canvasTerms, t]);

  // Section filter options
  const sectionOptions = useMemo(() => {
    if (!plan?.sections) return [];
    return sort(
      plan.sections
        .filter((s) => s.assessments && s.assessments.length > 0)
        .map((s) => s.name)
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i)
    );
  }, [plan]);

  // Filtered items
  const filteredItems = useMemo(() => {
    let items = collectionItems;

    // Goal/objective filter
    if (selectedGoalIds.length > 0 || selectedObjectiveIds.length > 0) {
      // Build a lookup: goalId -> set of all its objectiveIds
      const goalObjectivesMap = new Map();
      goals.forEach((goal) => {
        goalObjectivesMap.set(
          goal.id,
          new Set((goal.objectives || []).map((o) => o.id))
        );
      });

      items = items.filter((item) => {
        if (!item.goalId && !item.objectiveId) return false;

        if (item.goalId && selectedGoalIds.includes(item.goalId)) {
          if (item.objectiveId) {
            const goalObjs = goalObjectivesMap.get(item.goalId);
            if (goalObjs && goalObjs.has(item.objectiveId)) {
              return selectedObjectiveIds.includes(item.objectiveId);
            }
          } else {
            // Item has goal but no objective — check if "No objective" is selected
            const noObjKey = `${item.goalId}:${NO_OBJECTIVE_ID}`;
            return selectedObjectiveIds.includes(noObjKey);
          }
          return true;
        }

        if (
          item.objectiveId &&
          selectedObjectiveIds.includes(item.objectiveId)
        ) {
          return true;
        }

        // Check if "No objective" for this goal is selected (goal not selected)
        if (item.goalId && !item.objectiveId) {
          const noObjKey = `${item.goalId}:${NO_OBJECTIVE_ID}`;
          return selectedObjectiveIds.includes(noObjKey);
        }

        return false;
      });
    }

    // Section filter
    if (selectedSection) {
      items = items.filter((item) => item.sectionName === selectedSection);
    }

    return items;
  }, [
    collectionItems,
    selectedSection,
    selectedGoalIds,
    selectedObjectiveIds,
    goals,
  ]);

  // Grouped data for rendering
  const sectionGroups = useMemo(
    () => groupItemsBySection(filteredItems),
    [filteredItems]
  );
  const goalObjectiveGroups = useMemo(
    () => groupItemsByGoalObjective(filteredItems, goals),
    [filteredItems, goals]
  );

  // Summary metrics
  const summaryMetrics = useMemo(() => {
    const totalPlans = filteredItems.length;
    const termsWithData = filteredItems.reduce(
      (acc, item) => acc + item.completedTerms,
      0
    );
    const termsRemaining = filteredItems.reduce(
      (acc, item) => acc + (item.totalTerms - item.completedTerms),
      0
    );
    return { totalPlans, termsWithData, termsRemaining };
  }, [filteredItems]);

  // Computed: count of agenda-checked items visible in current filter
  const displayedSelectedCount = useMemo(() => {
    const agendaCheckedInFiltered = filteredItems.filter((item) =>
      agendaSourceIds.has(item.id)
    ).length;
    return selectedItems.length + agendaCheckedInFiltered;
  }, [filteredItems, agendaSourceIds, selectedItems]);

  // Computed: whether any filtered item is already in the agenda (disable all selection UI)
  const anyFilteredInAgenda = useMemo(() => {
    return filteredItems.some((item) => agendaSourceIds.has(item.id));
  }, [filteredItems, agendaSourceIds]);

  // Uncheck selectAll when filtered items change (filter applied/removed)
  const prevFilteredCountRef = useRef(filteredItems.length);
  useEffect(() => {
    if (filteredItems.length !== prevFilteredCountRef.current) {
      setSelectAll(false);
    }
    prevFilteredCountRef.current = filteredItems.length;
  }, [filteredItems]);

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      const selectableItems = filteredItems.filter(
        (item) => !agendaSourceIds.has(item.id)
      );
      setSelectedItems(selectableItems.map((item) => item.id));
      setSelectAll(true);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        setSelectAll(false);
        return prev.filter((id) => id !== itemId);
      }
      const newSelection = [...prev, itemId];
      const selectableCount = filteredItems.filter(
        (item) => !agendaSourceIds.has(item.id)
      ).length;
      if (newSelection.length === selectableCount) {
        setSelectAll(true);
      }
      return newSelection;
    });
  };

  const handleAddToAgenda = () => {
    if (!onAddToAgenda || selectedItems.length === 0) return;

    const selectedItemObjects = filteredItems.filter((item) =>
      selectedItems.includes(item.id)
    );

    const mappedItems = selectedItemObjects.map((item) => ({
      sourceId: item.id,
      sourceType: "assessment",
      sectionName: item.sectionName,
      itemName: item.measureName,
      goalId: item.goalId || null,
      goalDescription: item.goalDescription || "",
      objectiveId: item.objectiveId || null,
      objectiveDescription: item.objectiveDescription || "",
    }));

    onAddToAgenda(mappedItems);
    setSelectedItems([]);
    setSelectAll(false);
  };

  // Render term pills for a row
  const renderTermPills = (termStatuses) => {
    return (
      <Flex wrap="wrap" gap="x-small">
        {termStatuses.map((ts, idx) => {
          const color = ts.ended ? "success" : "primary";
          return (
            <Flex.Item key={idx}>
              <Pill
                color={color}
                renderIcon={ts.ended ? <IconCheckLine /> : undefined}
              >
                {ts.name}
              </Pill>
            </Flex.Item>
          );
        })}
      </Flex>
    );
  };

  // Progress color based on percentage
  const getProgressColor = (percentage) => {
    if (percentage === 100) return "success";
    if (percentage >= 50) return "warning";
    return "danger";
  };

  // Reusable table renderer for a subset of items
  const renderTable = (items, keyPrefix) => (
    <Table
      caption={t("assessmentPlanningDashboard.dataCollection.tableCaption")}
      layout="fixed"
    >
      <Table.Head>
        <Table.Row>
          <Table.ColHeader
            id={`${encodeURIComponent(keyPrefix)}-select`}
            width="2rem"
          >
            <ScreenReaderContent>
              {t("assessmentPlanningDashboard.dataCollection.selectMeasure")}
            </ScreenReaderContent>
          </Table.ColHeader>
          <Table.ColHeader
            id={`${encodeURIComponent(keyPrefix)}-measure`}
            width="25%"
          >
            {t("assessmentPlanningDashboard.columnMeasure")}
          </Table.ColHeader>
          <Table.ColHeader
            id={`${encodeURIComponent(keyPrefix)}-sources`}
            width="30%"
          >
            {t("assessmentPlanningDashboard.dataCollection.columnSources")}
          </Table.ColHeader>
          <Table.ColHeader
            id={`${encodeURIComponent(keyPrefix)}-terms`}
            width="25%"
          >
            {t(
              "assessmentPlanningDashboard.dataCollection.columnCollectionTerms"
            )}
          </Table.ColHeader>
          <Table.ColHeader
            id={`${encodeURIComponent(keyPrefix)}-progress`}
            width="8rem"
            textAlign="end"
          >
            {t("assessmentPlanningDashboard.dataCollection.columnProgress")}
          </Table.ColHeader>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {items.map((item) => (
          <Table.Row key={item.id}>
            <Table.Cell>
              <Checkbox
                label={
                  <ScreenReaderContent>
                    {`${t(
                      "assessmentPlanningDashboard.dataCollection.selectMeasure"
                    )} ${item.measureName}`}
                  </ScreenReaderContent>
                }
                size="small"
                checked={
                  selectedItems.includes(item.id) ||
                  agendaSourceIds.has(item.id)
                }
                onChange={() => handleSelectItem(item.id)}
                disabled={anyFilteredInAgenda}
              />
            </Table.Cell>
            <Table.Cell>
              <View as="div">
                <Text weight="bold" size="small">
                  {item.measureName}
                </Text>
                <br />
                <Text size="x-small" color="secondary">
                  {item.sectionName}
                </Text>
              </View>
            </Table.Cell>
            <Table.Cell>
              <View as="div">
                {item.courses.length > 0 && (
                  <>
                    <Text size="x-small" weight="bold">
                      {t("programs.courses")}
                    </Text>
                    <br />
                    <Text size="x-small">{item.courses.join(", ")}</Text>
                  </>
                )}
                {item.rubricName && (
                  <>
                    <br />
                    <Text size="x-small" color="secondary">
                      {t("assessmentPlanningDashboard.dataCollection.rubric")}:{" "}
                      {item.rubricName}
                    </Text>
                  </>
                )}
              </View>
            </Table.Cell>
            <Table.Cell>
              <View as="div">
                {renderTermPills(item.termStatuses)}
                <Text size="x-small" color="secondary">
                  {item.completedTerms}{" "}
                  {t(
                    "assessmentPlanningDashboard.dataCollection.ofTermsCollected",
                    { total: item.totalTerms }
                  )}
                </Text>
              </View>
            </Table.Cell>
            <Table.Cell textAlign="end">
              <View as="div">
                <Text
                  size="large"
                  weight="bold"
                  color={getProgressColor(item.percentage)}
                >
                  {item.percentage}%
                </Text>
                <View
                  as="div"
                  margin="xx-small 0 0 0"
                  height="4px"
                  background="secondary"
                  borderRadius="small"
                  overflowX="hidden"
                >
                  <View
                    as="div"
                    height="4px"
                    background={getProgressColor(item.percentage)}
                    width={`${item.percentage}%`}
                    borderRadius="small"
                  />
                </View>
              </View>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );

  // Section-grouped tables
  const renderSectionGroupedTables = () => {
    return sectionGroups.map((group) => (
      <View as="div" margin="medium 0" key={group.sectionName}>
        <Text weight="bold" size="small" transform="uppercase">
          {group.sectionName} &mdash; {group.items.length}
        </Text>
        {renderTable(group.items, `section-${group.sectionName}`)}
      </View>
    ));
  };

  // Goal/Objective-grouped tables
  const renderGoalObjectiveGroupedTables = () => {
    return goalObjectiveGroups.map((goalGroup) => (
      <View as="div" margin="medium 0" key={goalGroup.goalId}>
        <Text weight="bold">{goalGroup.goalDescription}</Text>
        {goalGroup.objectiveGroups.map((objGroup) => (
          <View
            as="div"
            margin="small 0 small medium"
            key={objGroup.objectiveId}
          >
            <Text weight="bold" size="small" color="secondary">
              {objGroup.objectiveDescription} &mdash; {objGroup.items.length}
            </Text>
            {renderTable(objGroup.items, `obj-${objGroup.objectiveId}`)}
          </View>
        ))}
      </View>
    ));
  };

  return (
    <View as="div" padding="small 0">
      {termsLoading ? (
        <Flex justifyItems="center" padding="large">
          <Spinner renderTitle={t("global.loading")} />
        </Flex>
      ) : (
        <>
          {/* Filter */}
          <Flex
            wrap="wrap"
            gap="small"
            margin="0 0 medium 0"
            alignItems="start"
          >
            {plan.include_goals_objs && (
              <Flex.Item>
                <GroupByToggle
                  value={effectiveGroupBy}
                  onChange={handleGroupByChange}
                />
              </Flex.Item>
            )}

            {effectiveGroupBy === "section" && (
              <Flex.Item>
                <SimpleSelect
                  renderLabel={t("assessmentPlanningDashboard.sectionFilter")}
                  value={selectedSection}
                  onChange={(_e, { value }) => setSelectedSection(value)}
                  width="180px"
                >
                  <SimpleSelect.Option id="dc-section-all" value="">
                    {t("assessmentPlanningDashboard.allSections")}
                  </SimpleSelect.Option>
                  {sectionOptions.map((section, idx) => (
                    <SimpleSelect.Option
                      key={section}
                      id={`dc-section-${idx}`}
                      value={section}
                    >
                      {section}
                    </SimpleSelect.Option>
                  ))}
                </SimpleSelect>
              </Flex.Item>
            )}

            {effectiveGroupBy === "goalObjective" && (
              <Flex.Item>
                <div style={{ minWidth: "280px" }}>
                  <GoalObjectiveTreeSelect
                    goals={goals}
                    onChange={handleGoalObjectiveChange}
                    interaction={goals.length > 0 ? "enabled" : "disabled"}
                    disabledTooltip={t(
                      "assessmentPlanningDashboard.authoringAndReview.goalObjectiveDisabledTooltip"
                    )}
                  />
                </div>
              </Flex.Item>
            )}
          </Flex>

          {/* Bulk action bar */}
          <Flex
            alignItems="center"
            gap="small"
            wrap="wrap"
            margin="0 0 medium 0"
            padding="small"
          >
            <Flex.Item>
              <Checkbox
                label={t("assessmentPlanningDashboard.selectAllVisible")}
                checked={selectAll}
                onChange={handleSelectAll}
                disabled={anyFilteredInAgenda}
              />
            </Flex.Item>
            <Flex.Item>
              <Text size="small" weight="bold" color="brand">
                {t("assessmentPlanningDashboard.selectedCount", {
                  count: displayedSelectedCount,
                })}
              </Text>
            </Flex.Item>
            <Flex.Item shouldGrow textAlign="end">
              <Button
                size="small"
                onClick={handleAddToAgenda}
                interaction={
                  anyFilteredInAgenda
                    ? "disabled"
                    : selectedItems.length > 0
                      ? "enabled"
                      : "disabled"
                }
              >
                {t("assessmentPlanningDashboard.addToAgenda")}
              </Button>
            </Flex.Item>
          </Flex>

          {/* Summary metrics - card style */}
          <View
            as="div"
            margin="0 0 medium 0"
            borderWidth="small"
            borderRadius="medium"
            padding="medium"
            background="secondary"
          >
            <MetricGroup>
              <Metric
                renderLabel={t(
                  "assessmentPlanningDashboard.dataCollection.quantitativeMeasuresLabel"
                )}
                renderValue={summaryMetrics.totalPlans}
              />
              <Metric
                renderLabel={t(
                  "assessmentPlanningDashboard.dataCollection.termsCollectedLabel"
                )}
                renderValue={summaryMetrics.termsWithData}
              />
              <Metric
                renderLabel={t(
                  "assessmentPlanningDashboard.dataCollection.termsPendingLabel"
                )}
                renderValue={summaryMetrics.termsRemaining}
              />
            </MetricGroup>
          </View>

          {/* Grouped tables */}
          {effectiveGroupBy === "section" && renderSectionGroupedTables()}
          {effectiveGroupBy === "goalObjective" &&
            renderGoalObjectiveGroupedTables()}

          {filteredItems.length === 0 && (
            <View as="div" padding="medium" textAlign="center">
              <Text>
                {t("assessmentPlanningDashboard.dataCollection.noItems")}
              </Text>
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default DataCollectionStatus;
